import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from 'app/core/user/user.model';
import { RestfulEndpoint } from '../../services/restful-endpoint.service';
import { PersonalizationService } from 'app/services/personalization.service';
import { OAuthService } from '../auth/oauth.service';
import { DEFAULT_USER_PERMISSIONS, PermissionsMap } from './default-user-permissions';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private pendingUserRequest: Promise<User> | null = null;
  private user: User | null = null;

  public permissions: PermissionsMap = DEFAULT_USER_PERMISSIONS;

  constructor(
    private _httpClient: HttpClient,
    private endPoint: RestfulEndpoint,
    public _persona: PersonalizationService,
    private oauth: OAuthService,
  ) {}

  updateUser(user: User) {
    return this._httpClient.patch<User>('api/common/user', { user }).pipe(
      map((response) => {
        this.setCurrentUser(response);
      }),
    );
  }

  getProjectBalance(): Observable<any> {
    return this.endPoint.post('get_balance', {});
  }

  setCurrentUser(user: User) {
    this.user = user;
    localStorage.setItem('user-data', JSON.stringify(user));
  }

  resetUser() {
    this.user = null;
    localStorage.removeItem('user-data');
  }

  setCurrentUserPermissions(user: User) {
    for (const permission of user.permissions) {
      this.permissions[permission] = true;
    }
  }

  setPersonalization(user: User) {
    // New Personalization Flow
    if (user.personalization_flag) {
      this._persona.usePersona = true;
      this._persona.init(
        user.personalization,
        user.personalization.secondary_name ?? null,
        user.client_id ?? null,
      );
    } else {
      this._persona.resetThemeToDefault(user.personalization?.secondary_name ?? null);
    }
  }

  getCurrentUser = async (): Promise<User> => {
    if (this.user) {
      return Promise.resolve(this.user);
    } else {
      if (this.pendingUserRequest === null) {
        this.pendingUserRequest = new Promise(async (resolve, reject) => {
          if (this.oauth.isSignedIn() === false) {
            await this.oauth.onSignIn;
          }

          this.endPoint.post('me', null).subscribe({
            next: (response) => {
              const user = response.user_profile;
              this.setCurrentUser(user);
              this.setCurrentUserPermissions(user);
              this.setPersonalization(user);
              resolve(user);
            },
            error: (err) => {
              this.pendingUserRequest = null;
              reject(err);
            },
          });
        });
      }
      return this.pendingUserRequest;
    }
  };

  async getUserId(): Promise<string | null> {
    try {
      const user = await this.getCurrentUser();
      return user.user_id;
    } catch (error) {
      console.log(error);
    }
    return null;
  }

  getLoggedInUser(): User {
    return this.user;
  }

  /**
   * Check if the current user is an NWC client
   * @param projectSharedBy - Optional project's shared_by value to also check project-level NWC status
   * @returns true if user is NWC client or project is shared by NWC
   */
  async isNwcClient(projectSharedBy?: string): Promise<boolean> {
    const user = await this.getCurrentUser();
    const isUserNwc = user?.unifier_project_adapter === 'nwc';
    const isProjectNwc = projectSharedBy === 'nwc';
    return isUserNwc || isProjectNwc;
  }

  /**
   * Synchronous version of isNwcClient - uses cached user data
   * @param projectSharedBy - Optional project's shared_by value to also check project-level NWC status
   * @returns true if user is NWC client or project is shared by NWC
   */
  isNwcClientSync(projectSharedBy?: string): boolean {
    const isUserNwc = this.user?.unifier_project_adapter === 'nwc';
    const isProjectNwc = projectSharedBy === 'nwc';
    return isUserNwc || isProjectNwc;
  }
}
